import './movement.scss'

import * as _ from 'lodash'
import { defineComponent } from 'vue'
import { combineLatest } from 'rxjs'
import {
    faAngleDown,
    faAngleRight,
    faCircleNotch,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'

import { LinkableItemLifecycleStates, Item, Movement, MovementLifecycleStates, ThesaurusTerm, Featured, Task, Container, ItemTypeWrapper, getTranslatedString, LinkableArchiveItemResource, SpecimenResource } from '@skinsoft/types'
import { TrackingRoutes } from '@skinsoft/const'
import { itemSelectors, locationSelectors, movementSelectors, specimenSelectors, taskSelectors, thesaurusTermSelectors } from '@skinsoft/selectors'
import {
    itemSorters,
    MovementDataStore,
    specimenSorters,
} from '@skinsoft/entity'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { ContainerCard, MovementFeaturedCard, MovementItemCard, MovementSpecimenCard } from '@skinsoft/entity-ui'
import { ItemCard, PullToRight, SpecimenCard } from '@skinsoft/ui'

import {
    ADD_ACCESSORY_TOPIC,
    ADD_CONTAINER_TOPIC,
    ADD_ITEM_TOPIC,
    ADD_SPECIMEN_TOPIC,
    CANCEL_MOVEMENT_TOPIC,
    CLOSE_MOVEMENT_TOPIC,
    DELETE_MOVEMENT_TOPIC,
    RETURN_MOVEMENT_TOPIC,
    START_ITEM_TRANSIT_TOPIC,
    TURN_ON_BARCODE_READER_TOPIC,
} from '../../../busTopics'
import { CloseMovementModal } from '../../component/close-movement-modal'
import { BAR_CODE_READER_CLICK } from '@skinsoft/entity-ui/src/movement/component/movement-item-card/identifier-input'
import { map, tap } from 'rxjs/operators'
import { SelectItemMultipleModal } from '../../component/select-item-multiple-modal'
import {
    getClassNameFromContentTypeKey,
    getContentTypeFromTypeKey,
    ListContentType,
} from '../container/list-content-type'
import { DELETE_CARD, MOVEMENT_ITEM_CARD_LOCATION_CHANGED, UPDATE_ITEM_MOVEMENT_PAGE } from '@skinsoft/events'
import { ACCESSORY_TYPES_FOR_SEARCH, CONTAINER_TYPES_FOR_SEARCH, DataStore } from '@skinsoft/data-store'
import { BModal } from 'bootstrap-vue'
import { STOP_ASKING_FOR_MISSING_LOCATION_TOPIC } from '@skinsoft/bus'

type ItemSorts = 'ITEM' | 'CONTAINER' | 'ACCESSORY' | 'SPECIMEN'

export const MovementPage = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sSelectItemMultipleModal: SelectItemMultipleModal,
        sCloseMovementModal: CloseMovementModal,
        sItemCard: ItemCard,
        sSpecimenCard: SpecimenCard,
        sMovementFeaturedCard: MovementFeaturedCard,
        sMovementItemCard: MovementItemCard,
        sMovementSpecimenCard: MovementSpecimenCard,
        sPullToRight: PullToRight,
        sContainerCard: ContainerCard,
    },
    template: require('./movement.html'),
    data() {
        return {
            angleDownIcon: faAngleDown,
            angleRightIcon: faAngleRight,
            spinnerIcon: faCircleNotch,
            movement: null as any as Movement,
            motive: null as any as ThesaurusTerm,
            associatedTask: null as any as Task,
            currentlyAddingTo: null as ListContentType | null,
            ListContentType: ListContentType,

            itemTypes: ['physicalMuseumItem', 'numericMuseumItem'],
            specimenTypes: ['specimen'],
            accessoryTypes: ACCESSORY_TYPES_FOR_SEARCH,
            containerTypes: CONTAINER_TYPES_FOR_SEARCH,

            movementCreationPending: false,
            showCollapse: false,
            addItemModalMounted: false,
            addSpecimenModalMounted: false,
            addAccessoryModalMounted: false,
            addContainerModalMounted: false,
            exclamationTriangleIcon: faExclamationTriangle,
            closeMovementModalMounted: false,
            featured: null as any as Featured,
            focusedItemId: null as any as Item['id'],
            items: [] as any[],
            specimens: [] as SpecimenResource[],
            containers: [] as ItemTypeWrapper[], // ContainerCard needs ItemTypeWrapper
            accessories: [] as Item[],

            committedItems: [] as string[],

            DELETE_CARD: DELETE_CARD,
            MOVEMENT_ITEM_CARD_LOCATION_CHANGED: MOVEMENT_ITEM_CARD_LOCATION_CHANGED,
            UPDATE_ITEM_MOVEMENT_PAGE: UPDATE_ITEM_MOVEMENT_PAGE,
        }
    },
    computed: {
        title(): any {
            return movementSelectors.title(this.movement)
        },

        associatedTaskIncrementalTitle(): any {
            return taskSelectors.incrementalTitle(this.associatedTask)
        },

        linkableItems(): (LinkableArchiveItemResource | undefined)[] | undefined {
            if (this.movement) {
                return movementSelectors.linkableItems(this.movement).map(item => item.linkableItem)
            }
        },

        state(): MovementLifecycleStates | undefined | null {
            return movementSelectors.state(this.movement)
        },
    },
    mounted() {
        this.subscribeToBus(ADD_ITEM_TOPIC, this.addItem.bind(this))
        this.subscribeToBus(ADD_SPECIMEN_TOPIC, this.addSpecimen.bind(this))
        this.subscribeToBus(CANCEL_MOVEMENT_TOPIC, this.cancelMovement.bind(this))
        this.subscribeToBus(CLOSE_MOVEMENT_TOPIC, this.closeMovement.bind(this))
        this.subscribeToBus(DELETE_MOVEMENT_TOPIC, this.deleteMovement.bind(this))
        this.subscribeToBus(RETURN_MOVEMENT_TOPIC, this.askConfirmationForReturnMovement.bind(this))
        this.subscribeToBus(BAR_CODE_READER_CLICK, () =>
            this.emitOnBus(TURN_ON_BARCODE_READER_TOPIC),
        )
        this.subscribeToBus(START_ITEM_TRANSIT_TOPIC, this.startItemTransit.bind(this))
        this.subscribeToBus(ADD_CONTAINER_TOPIC, this.addContainer.bind(this))
        this.subscribeToBus(ADD_ACCESSORY_TOPIC, this.addAccessory.bind(this))

        this.subscribeToMovement()

        this.$emit('noFocus')
    },
    methods: {
        subscribeToMovement() {
            this.subscribeTo(
                this.$movementDataStore.fetchOne(this.$route.params.id as string),
                movement => {
                    this.movement = movement
                },
                'movement',
            )
        },

        subscribeToMotive() {
            const motiveId = movementSelectors.motive(this.movement)

            if (!motiveId) {
                return
            }

            this.subscribeTo(
                this.$thesaurusTermDataStore.fetchOne(motiveId),
                thesaurusTerm => {
                    this.motive = getTranslatedString(
                        thesaurusTermSelectors.preferredTerm(thesaurusTerm),
                        this.getAppLocale()
                    )
                },
                'motive',
            )
        },

        subscribeToAssociatedTask() {
            const associatedTaskId = movementSelectors.associatedTasks(this.movement)[0]

            if (!associatedTaskId) {
                return
            }

            this.subscribeTo(
                this.$taskDataStore.fetchOne(associatedTaskId),
                associatedTask => {
                    this.associatedTask = associatedTask
                },
                'associatedTask',
            )
        },

        subscribeToItems() {
            const linkableItems = movementSelectors.linkableItems(this.movement)
            const state = movementSelectors.state(this.movement)

            linkableItems.filter(linkableItem => {
                switch (state) {
                    default:
                        return true

                    case MovementLifecycleStates.FINALIZED:
                    case MovementLifecycleStates.ARCHIVED:
                    case MovementLifecycleStates.CANCELED:
                        return (
                            linkableItem.state &&
                            linkableItem.state.name === LinkableItemLifecycleStates.FINALIZED
                        )
                }
            })

            if (!linkableItems || linkableItems.length === 0) {
                this.items = []
                this.containers = []
                this.accessories = []
                return
            }

            const itemIds = linkableItems.filter(
                    linkableItemResource => 
                        linkableItemResource.linkableItem?.linkableItem 
                        && linkableItemResource.linkableItem?.linkableItemReferenceType?.toLowerCase().includes('item')
                ).map(
                    linkableItemResource => 
                        linkableItemResource.linkableItem!.linkableItem as string
                )

            this.subscribeTo(
                combineLatest(
                    _.uniq(itemIds).map(item =>
                        this.$itemDataStore.fetchIfHasAccess(item),
                    ),
                ).pipe(
                    map(items => items.filter(_.identity) as Item[]),
                    tap(
                        items =>
                            (this.containers = items
                                .filter(item => item.type.includes('Container'))
                                .map(item => {
                                    return {
                                        item: item,
                                        wrapperType: 'Item',
                                        itemType: 'Container',
                                    } as ItemTypeWrapper
                                })
                            ),
                    ),
                    map(items => items.filter(item => !item.type.includes('Container'))),
                    tap(
                        items =>
                            (this.accessories = items.filter(item => item.type.includes('Accessory'))),
                    ),
                    map(items => items.filter(item => !item.type.includes('Accessory'))),
                    tap(() => (this.currentlyAddingTo = null)),
                ),
                items => {
                    this.items = items.sort(itemSorters.byRealTimeLocation) as Item[]
                },
                'items',
            )
        },

        subscribeToSpecimens() {
            const linkableItems = movementSelectors.linkableItems(this.movement)
            const state = movementSelectors.state(this.movement)

            linkableItems.filter(linkableItem => {
                switch (state) {
                    default:
                        return true

                    case MovementLifecycleStates.FINALIZED:
                    case MovementLifecycleStates.ARCHIVED:
                    case MovementLifecycleStates.CANCELED:
                        return (
                            linkableItem.state &&
                            linkableItem.state.name === LinkableItemLifecycleStates.FINALIZED
                        )
                }
            })

            if (!linkableItems || linkableItems.length === 0) {
                this.specimens = []
                return
            }

            const specimenIds = linkableItems.filter(
                    linkableItemResource => linkableItemResource.linkableItem?.linkableItem
                    && linkableItemResource.linkableItem?.linkableItemReferenceType?.toLowerCase().includes('specimen')
                ).map(
                    linkableItemResource => linkableItemResource.linkableItem!.linkableItem as string
                )

            this.subscribeTo(
                combineLatest(
                    _.uniq(specimenIds).map(specimen =>
                        this.$specimenDataStore.fetchIfHasAccess(specimen)
                    ),
                ).pipe(
                    map(specimens => specimens.filter(_.identity) as SpecimenResource[])),
                specimens => {
                    this.specimens = specimens.sort(specimenSorters.byRealTimeLocation) as SpecimenResource[]
                },
                'specimens',
            )
        },

        onRouteChange() {
            // to hide the reset of the puller, we reset the state of this page to make it look like the same as the mockup view
            this.items = [null, null, null] as any[]
            this.specimens = [] as SpecimenResource[]
            ;(this.$refs.puller as InstanceType<typeof PullToRight>).reset()

            this.refreshMovement()

            this.focusedItemId = null as any
        },

        onEntityChange() {
            this.unsubscribeFrom('motive')
            this.unsubscribeFrom('associatedTask')
            this.unsubscribeFrom('contents')
            this.unsubscribeFrom('items')
            this.unsubscribeFrom('specimens')
            this.subscribeToAssociatedTask()
            this.subscribeToMotive()
            this.subscribeToItems()
            this.subscribeToSpecimens()
        },

        onPulled() {
            // the user has reach pull-right non return point, we must transition to the next entity
            const next = this.$route.query.next as any
            const id = (next as string[]).shift() as string

            requestAnimationFrame(() =>
                this.$router.push({
                    name: this.$route.name || '',
                    params: { id },
                    query: (next.length > 0 ? { next } : {}) as Record<string, string>,
                }),
            )
        },

        focusItem(item: Item) {
            this.focus(itemSelectors.id(item))
        },

        focusSpecimen(specimen: SpecimenResource) {
            this.focus(specimenSelectors.id(specimen))
        },

        focus(id: any) {
            if (this.committedItems.indexOf(id) !== -1) {
                return // we can't focus already validated items
            }

            this.$emit('focusedItem')

            this.focusedItemId = id
        },

        rollbackItem(item: Item) {
            if (!this.isItemCommitted(item)) {
                return
            }

            this.rollback(itemSelectors.id(item))
        },

        rollbackSpecimen(specimen: SpecimenResource) {
            if (!this.isSpecimenCommitted(specimen)) {
                return
            }

            this.rollback(specimenSelectors.id(specimen))
        },

        rollback(id: any) {
            const index = this.committedItems.indexOf(id)
            this.committedItems.splice(index, 1)
        },

        commitItem(item: Item) {
            if (this.isItemCommitted(item)) {
                return
            }

            this.commit(itemSelectors.id(item))
        },

        commitSpecimen(specimen: SpecimenResource) {
            if (this.isSpecimenCommitted(specimen)) {
                return
            }

            this.commit(specimenSelectors.id(specimen))
        },

        commit(id: any) {
            if (this.focusedItemId === id) {
                this.unfocusItem() // we can't focus already validated items
            }

            this.committedItems.push(id)
        },

        unfocusItem() {
            this.focusedItemId = null as any
            this.$emit('noFocus')
        },

        hasItemFocus(item: Item) {
            return this.focusedItemId === itemSelectors.id(item)
        },

        hasSpecimenFocus(specimen: SpecimenResource) {
            return this.focusedItemId === specimenSelectors.id(specimen)
        },

        isItemCommitted(item: Item) {
            return this.committedItems.indexOf(itemSelectors.id(item)) !== -1
        },

        isSpecimenCommitted(specimen: SpecimenResource) {
            return this.committedItems.indexOf(specimenSelectors.id(specimen)) !== -1
        },

        toggleCollapse() {
            this.showCollapse = !this.showCollapse
        },

        goToAssociatedTask(event: MouseEvent) {
            event.preventDefault()
            event.stopPropagation()

            if (!this.associatedTask) {
                return
            }
            this.$router.push(`/tasks/${this.associatedTask.id}`)
        },

        /**
         * Add item to movement's linkableItems and refresh movement data
         * @param item
         */
        async onAddedItemSelected(item: { id: string; type: string }) {
            try {
                switch (item.type.toLowerCase()) {
                    case 'specimen':
                        this.currentlyAddingTo = ListContentType.SPECIMEN
                        await this.$movementDataStore.addItem(
                            this.movement.id,
                            item.id,
                            'specimen',
                        )
                        break
                    default:
                        this.currentlyAddingTo = getContentTypeFromTypeKey(item.type)
                        await this.$movementDataStore.addItem(
                            this.movement.id,
                            item.id,
                            getClassNameFromContentTypeKey(item.type),
                        )
                        break
                }
                this.unsubscribeFrom('movement')
                this.subscribeToMovement()
            } catch (error) {
                console.error(error)
                this.currentlyAddingTo = null
            } finally {
                (this.$refs.addItemModal as InstanceType<typeof SelectItemMultipleModal>)?.resetSearch();
                (this.$refs.addSpecimenModal as InstanceType<typeof SelectItemMultipleModal>)?.resetSearch();
                (this.$refs.addAccessoryModal as InstanceType<typeof SelectItemMultipleModal>)?.resetSearch();
                (this.$refs.addContainerModal as InstanceType<typeof SelectItemMultipleModal>)?.resetSearch()
            }
        },

        askConfirmationForReturnMovement() {
            (this.$refs.returnMovementModal as InstanceType<typeof BModal>).show()
        },

        async createReturnMovement() {
            this.movementCreationPending = true

            let movement: Movement
            try {
                movement = await (this.$movementDataStore as MovementDataStore).createReturnMovement(
                    this.movement.id,
                )
            } finally {
                this.movementCreationPending = false
            }

            if (movement) {
                this.$router.push({
                    name: this.$route.name || '',
                    params: { id: movement.id },
                })
            }
        },

        addItem() {
            this.unfocusItem() // to prevent from filling the input if scan mode is enable
            ;(this.$refs.addItemModal as InstanceType<typeof SelectItemMultipleModal>).open()
        },

        addSpecimen() {
            this.unfocusItem() // To prevent from filling the input if scan mode is enabled
            ;(this.$refs.addSpecimenModal as InstanceType<typeof SelectItemMultipleModal>).open()
        },

        addContainer() {
            (this.$refs.addContainerModal as InstanceType<typeof SelectItemMultipleModal>).open()
        },

        addAccessory() {
            this.unfocusItem() // to prevent from filling the input if scan mode
            ;(this.$refs.addAccessoryModal as InstanceType<typeof SelectItemMultipleModal>).open()
        },

        closeMovement() {
            this.unfocusItem() // to prevent from filling the input if scan mode is enable
            if (this.$refs.closeMovementModal) {
                (this.$refs.closeMovementModal as InstanceType<typeof CloseMovementModal>).open()
            }
        },

        onMovementClosed() {
            this.emitOnBus(STOP_ASKING_FOR_MISSING_LOCATION_TOPIC)
            this.refreshMovement()
        },

        cancelMovement() {
            this.waitForUserApproval(this.$t('entity.cancelMovement') as string)

            return (this.$movementDataStore as MovementDataStore).cancelMovement(this.movement.id)
        },

        deleteMovement() {
            if(this.movement.id) {
                this.$movementDataStore.delete(this.movement.id).subscribe(() => {
                    this.$router.push({ name: TrackingRoutes.MOVEMENTS })
                })
            }
        },

        refreshMovement() {
            this.unsubscribeFrom('movement')
            this.subscribeToMovement()
        },

        async startItemTransit() {
            if (this.linkableItems) {
                await this.$movementDataStore.transitionLinkableItems(
                    this.movement,
                    LinkableItemLifecycleStates.IN_TRANSIT,
                    undefined, // undefined for transitionning all items ([] is not considered undefined)
                )
                this.refreshMovement()
            }
        },

        async onItemLocationChanged(event) {
            if (event.itemId && event.location) {
                if (this.movement) {
                    await this.$movementDataStore.transitionLinkableItem(
                        movementSelectors.id(this.movement),
                        event.itemId,
                        LinkableItemLifecycleStates.FINALIZED,
                        locationSelectors.id(event.location),
                    )
                    this.refreshMovement()
                }
            }
        },

        onItemUpdated(event, itemType: ItemSorts) {
            // @ts-ignore
            let dataStore: DataStore
            let list: (Item | ItemTypeWrapper | SpecimenResource)[]

            switch(itemType) {
                case 'CONTAINER':
                    dataStore = this.$containerDataStore
                    list = this.containers
                    break
                case 'SPECIMEN':
                    dataStore = this.$specimenDataStore
                    list = this.specimens
                    break
                case 'ACCESSORY':
                    dataStore = this.$itemDataStore
                    list = this.accessories
                    break
                default:
                    dataStore = this.$itemDataStore
                    list = this.items
                    break
            }

            if(event.itemId && event.changes) {

                if(itemType === 'CONTAINER') {
                    const typedList = list as ItemTypeWrapper[]
                    const itemToUpdateIndex = typedList.findIndex(item => (item.item as Container).id === event.itemId)
                    if(itemToUpdateIndex >= 0) {
                        const body = dataStore.mergeOverridingArrays(
                            typedList[itemToUpdateIndex].item,
                            event.changes
                        )

                        dataStore.update(
                            (typedList[itemToUpdateIndex].item as Container).id,
                            body
                        )
                        .subscribe(itemUpdated => {
                            // works because list is a reference ("pointer") on this.containers, ...
                            list.splice(
                                itemToUpdateIndex,
                                1,
                                {
                                    item: itemUpdated,
                                    wrapperType: 'Item',
                                    itemType: 'Container',
                                }
                            )
                        })
                    }
                } else if (itemType === 'SPECIMEN') {
                    const typedList = list as ItemTypeWrapper[]
                    const specimenToUpdateIndex = typedList.findIndex(specimen => (specimen.item as SpecimenResource).id === event.itemId)
                    if(specimenToUpdateIndex >= 0) {
                        const body = dataStore.mergeOverridingArrays(
                            typedList[specimenToUpdateIndex].item,
                            event.changes
                        )

                        dataStore.update(
                            (typedList[specimenToUpdateIndex].item as SpecimenResource).id,
                            body
                        )
                        .subscribe(itemUpdated => {
                            list.splice(
                                specimenToUpdateIndex,
                                1,
                                {
                                    item: itemUpdated,
                                    wrapperType: 'Specimen',
                                    itemType: 'Specimen',
                                }
                            )
                        })
                    }                    
                } else {
                    const typedList = list as Item[]
                    const itemToUpdateIndex = typedList.findIndex(item => item.id === event.itemId)
                    if(itemToUpdateIndex >= 0) {
                        const body = dataStore.mergeOverridingArrays(typedList[itemToUpdateIndex], event.changes)

                        dataStore.update(
                            typedList[itemToUpdateIndex].id,
                            body
                        )
                        .subscribe((itemUpdated) => {
                            list.splice(itemToUpdateIndex, 1, itemUpdated) // works because list is a reference ("pointer") on this.items or this.containers, ...
                        })
                    }
                }
            }
        },

        toggleCloseContainer(container: Container) {
            this.containers.splice(
                this.containers.findIndex(listItem => container.id === (listItem.item as Container).id),
                1,
                {
                    item: container,
                    itemType: 'Container',
                    wrapperType: 'Item',
                } as ItemTypeWrapper,
            )
        },

        async removeItem(itemId: string) {
            this.$movementDataStore.removeItem(this.movement.id, itemId).subscribe(this.refreshMovement)
        },
    },
    watch: {
        '$route': {
            handler: 'onRouteChange',
            deep: true,
        },
        movement: {
            handler: 'onEntityChange',
        },
    },
})
